import { useState } from 'react';
import { createFileRoute, redirect } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Button, Card, Spinner } from '@mkcore/ui';
import { meQuery } from '@/lib/session';

/**
 * OAuth consent for MCP clients (CO-0110): the user sees which client asks for access and decides. The decision goes
 * to Better Auth with the signed query of the authorize request.
 */
export const Route = createFileRoute('/consent')({
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(meQuery);
    if (!me) throw redirect({ to: '/login', search: { redirect: window.location.pathname + window.location.search } });
  },
  component: Consent,
});

function Consent() {
  const { t } = useTranslation();
  const query = window.location.search.slice(1);
  const params = new URLSearchParams(query);
  const clientId = params.get('client_id') ?? '';
  const scopes = (params.get('scope') ?? '').split(' ').filter(Boolean);
  const [busy, setBusy] = useState(false);
  const client = useQuery({
    queryKey: ['oauth-client', clientId],
    queryFn: async () => {
      const r = await fetch(`/api/auth/oauth2/public-client?client_id=${encodeURIComponent(clientId)}`, { credentials: 'same-origin' });
      return r.ok ? ((await r.json()) as { client_name?: string }) : {};
    },
    enabled: !!clientId,
  });

  const decide = async (accept: boolean) => {
    setBusy(true);
    const res = await fetch('/api/auth/oauth2/consent', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ accept, oauth_query: query }),
    });
    const body = (await res.json().catch(() => ({}))) as { url?: string; redirect_uri?: string };
    const target = body.url ?? body.redirect_uri;
    if (target) window.location.assign(target);
    else setBusy(false);
  };

  const name = client.data?.client_name ?? clientId;
  return (
    <main className="grid min-h-dvh place-items-center bg-canvas p-4">
      <Card className="grid w-full max-w-md gap-5 p-8">
        {client.isLoading ? (
          <Spinner label={t('common.loading')} />
        ) : (
          <>
            <h1 className="text-xl font-bold">{t('auth.consentTitle', { client: name })}</h1>
            <p className="text-sm text-fg-2">{t('auth.consentText')}</p>
            <div>
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-fg-3">{t('auth.consentScopes')}</p>
              <ul className="flex flex-wrap gap-1.5">
                {scopes.map((s) => (
                  <li key={s} className="rounded-sm bg-sunken px-2 py-0.5 font-mono text-xs">
                    {s}
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => void decide(false)} disabled={busy}>
                {t('auth.deny')}
              </Button>
              <Button onClick={() => void decide(true)} loading={busy}>
                {t('auth.allow')}
              </Button>
            </div>
          </>
        )}
      </Card>
    </main>
  );
}
